import { Link } from "@tanstack/react-router";
import { ArrowLeft, Plus } from "lucide-react";
import { useMemo, type ReactNode } from "react";
import {
  Button,
  buttonVariants,
  Card,
  SearchInput,
  Skeleton,
  Tooltip,
} from "@rumbledirect/ui-v2";
import { useGetRootAffiliates } from "@/lib/api/generated/affiliate/affiliate";
import { cn } from "@rumbledirect/ui-v2/cn";
import type { AffiliateListItem } from "./affiliate";

const SKELETON_ROW_KEYS = ["affiliate-skeleton-0", "affiliate-skeleton-1"];

function countLabel(isLoading: boolean, count: number): string {
  if (isLoading) return "Affiliates ophalen…";
  return count === 1 ? "1 affiliate" : `${count} affiliates`;
}

/** The list's `?q=` search param, kept in the URL so it survives selection. */
export interface AffiliatesSearch {
  readonly q?: string;
}

export function parseAffiliatesSearch(
  search: Record<string, unknown>,
): AffiliatesSearch {
  return {
    q: typeof search.q === "string" && search.q !== "" ? search.q : undefined,
  };
}

export interface AffiliatesPageProps {
  readonly query: string;
  readonly onQueryChange: (query: string) => void;
  /** The affiliate whose detail pane is open, for the list's active state. */
  readonly selectedId?: string;
  /** Marks the create destination active, since it has no id to match on. */
  readonly creating?: boolean;
  /** The detail pane. Absent on the list route, which shows a hint instead. */
  readonly children?: ReactNode;
}

/**
 * The Affiliates workspace: a searchable list on the left, the selected
 * affiliate on the right. All three affiliate routes render this and pass
 * their own detail pane as `children`, so the list keeps its scroll, its
 * search and its fetched data across selection.
 *
 * Below `lg` there is no room for both, so the list and the pane become
 * separate screens and the pane carries its own back link.
 */
export function AffiliatesPage({
  query,
  onQueryChange,
  selectedId,
  creating = false,
  children,
}: AffiliatesPageProps): ReactNode {
  const { data, isPending, isError, isFetching, refetch } =
    useGetRootAffiliates();

  const members = useMemo(() => {
    const rows: readonly AffiliateListItem[] = data?.member ?? [];
    const normalizedQuery = query.trim().toLocaleLowerCase("nl");
    const filtered =
      normalizedQuery === ""
        ? rows
        : rows.filter((affiliate) =>
            `${affiliate.name} ${affiliate.slug} ${affiliate.email}`
              .toLocaleLowerCase("nl")
              .includes(normalizedQuery),
          );

    return [...filtered].sort((a, b) => a.name.localeCompare(b.name, "nl"));
  }, [data, query]);

  const hasSelection = selectedId !== undefined || creating;
  const search: AffiliatesSearch = { q: query === "" ? undefined : query };

  return (
    <div className="space-y-6">
      <header className={cn(hasSelection && "hidden lg:block")}>
        <Link
          to="/platform-management"
          className="mb-3 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="size-4" aria-hidden="true" />
          Platformbeheer
        </Link>
        <h1 className="text-2xl font-semibold tracking-tight">Affiliates</h1>
      </header>

      <div className="grid items-start gap-6 lg:grid-cols-[300px_minmax(0,1fr)]">
        <Card
          className={cn(
            "lg:sticky lg:top-6",
            hasSelection && "hidden lg:block",
          )}
          aria-busy={isFetching}
        >
          <div className="flex items-center gap-2 border-b border-border p-4">
            <SearchInput
              aria-label="Zoek affiliates"
              placeholder="Zoek een affiliate…"
              className="min-w-0 flex-1"
              value={query}
              onChange={(event) => onQueryChange(event.target.value)}
              onClear={() => onQueryChange("")}
            />
            <Tooltip label="Nieuwe affiliate" side="top" className="shrink-0">
              <Link
                to="/platform-management/affiliates/new"
                search={search}
                aria-label="Nieuwe affiliate"
                aria-current={creating ? "page" : undefined}
                className={buttonVariants({
                  variant: creating ? "primary" : "outline",
                  size: "icon",
                })}
              >
                <Plus className="size-4" aria-hidden="true" />
              </Link>
            </Tooltip>
          </div>

          <AffiliateList
            isPending={isPending}
            isError={isError}
            members={members}
            selectedId={selectedId}
            search={search}
            onRetry={() => void refetch()}
            onClearQuery={() => onQueryChange("")}
          />

          {/*
            Counts what the list actually shows. The server's `totalItems`
            would contradict it, because the search filters client-side.
          */}
          {!isError && (
            <div className="border-t border-border px-4 py-3 text-xs text-muted-foreground">
              {countLabel(data === undefined, members.length)}
            </div>
          )}
        </Card>

        <section
          aria-label={creating ? "Nieuwe affiliate" : "Affiliategegevens"}
          className={cn("min-w-0", !hasSelection && "hidden lg:block")}
        >
          {children ?? (
            <Card className="flex min-h-96 items-center justify-center p-8">
              <p className="text-sm text-muted-foreground">
                Selecteer een affiliate om de gegevens te beheren.
              </p>
            </Card>
          )}
        </section>
      </div>
    </div>
  );
}

function AffiliateList({
  isPending,
  isError,
  members,
  selectedId,
  search,
  onRetry,
  onClearQuery,
}: {
  readonly isPending: boolean;
  readonly isError: boolean;
  readonly members: readonly AffiliateListItem[];
  readonly selectedId?: string;
  readonly search: AffiliatesSearch;
  readonly onRetry: () => void;
  readonly onClearQuery: () => void;
}): ReactNode {
  if (isPending) {
    return (
      <div
        role="status"
        aria-live="polite"
        aria-label="Affiliates laden"
        className="space-y-3 p-4"
      >
        {SKELETON_ROW_KEYS.map((key) => (
          <Skeleton key={key} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <div role="alert" className="space-y-3 p-4 text-sm">
        <p className="text-muted-foreground">
          We konden de affiliates niet laden.
        </p>
        <Button variant="outline" size="sm" onClick={onRetry}>
          Opnieuw proberen
        </Button>
      </div>
    );
  }

  if (members.length === 0) {
    return (
      <div className="space-y-3 p-5 text-sm">
        <p className="text-muted-foreground">Geen affiliates gevonden.</p>
        <Button variant="ghost" size="sm" onClick={onClearQuery}>
          Zoekopdracht wissen
        </Button>
      </div>
    );
  }

  return (
    <nav aria-label="Affiliates" className="max-h-[60vh] overflow-y-auto">
      <ul className="divide-y divide-border">
        {members.map((affiliate) => (
          <li key={affiliate.id}>
            <Link
              to="/platform-management/affiliates/$affiliateId"
              params={{ affiliateId: affiliate.id }}
              search={search}
              aria-current={affiliate.id === selectedId ? "page" : undefined}
              className={cn(
                "block border-l-2 px-4 py-4 text-sm font-medium transition-colors hover:bg-muted/50 focus-visible:-outline-offset-2",
                affiliate.id === selectedId
                  ? "border-primary bg-primary-soft text-accent-foreground"
                  : "border-transparent text-foreground",
              )}
            >
              {affiliate.name}
            </Link>
          </li>
        ))}
      </ul>
    </nav>
  );
}
